import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { Vehicle } from '../models/core.models';
import { VehiclesFirebaseService } from '../services/firebase';

/**
 * Resolves the "similar vehicles" candidate pool (same organisation or same
 * type) for /motorhome/:id so the related-listings cards render in the
 * server-rendered HTML (HMM-162 IL-2). The Router awaits resolvers before the
 * route renders, which makes the read deterministic under request-time SSR —
 * fetching inside the child component started after the SSR stability snapshot
 * (the component only mounts once the page's vehicle load resolves) and left
 * the section empty.
 *
 * Reads the full active set via `getActiveVehiclesOnce()` — a REST `getDocs`
 * one-shot query (no WebChannel listener) wrapped in `trackPending`, so SSR
 * awaits it before snapshotting the HTML — and filters client-side. The
 * single-doc `getByIdOnce` read resolves the same way.
 *
 * Returns the de-duplicated pool; the component ranks it (excluding the
 * current vehicle) and limits the count.
 */
export const motorhomeSimilarResolver: ResolveFn<Vehicle[]> = (route: ActivatedRouteSnapshot) => {
  const vehiclesService = inject(VehiclesFirebaseService);
  const id = route.paramMap.get('id') ?? '';
  if (!id) return of([] as Vehicle[]);

  return vehiclesService.getByIdOnce(id).pipe(
    switchMap((vehicle) => {
      if (!vehicle) return of([] as Vehicle[]);

      const orgId = vehicle.organizationId;
      const type = vehicle.type;
      return vehiclesService.getActiveVehiclesOnce().pipe(
        take(1),
        map((all) =>
          all.filter((v) => v.id !== id && (v.organizationId === orgId || v.type === type)),
        ),
      );
    }),
    take(1),
    timeout(8000), // Don't hang the render if Firestore is slow/unreachable.
    catchError(() => of([] as Vehicle[])),
  );
};
